import { createSlice, createSelector } from '@reduxjs/toolkit'

import { DrawerState } from 'helpers/drawer-state-enum'
import { LookupFormat } from 'helpers/lookup-format-enum'
import { Settings } from 'helpers/settings-enum'

export type PanelState = 'top' | 'left' | 'right'
export type Tab = {
    name: string,
    icon: string,
    disabled?: boolean,
    visible?: boolean,
    userfriendlyName?: string,
}

export const settingsMenuSelector = createSelector(
    [
        (state: any) => state.ui.settings,
        (_: any, name: string) => name
    ],
    (settings: any, name: string): any => {
        return settings.find((setting: any) => setting.settingName === name) || {id: '', settingName: '', settingKey: '', value: ''}
    }
) 


// exported for testing
export const getInitialState = () => ({
    settings: [
        {id: '', settingName: Settings.lookupThreshold, settingKey: 'ui-config', value: 20},
        {id: '', settingName: Settings.dropDownHeight, settingKey: 'ui-config', value: 600},
        {id: '', settingName: Settings.objNumber, settingKey: 'ui-config', value: 5},
        {id: '', settingName: Settings.unitNumber, settingKey: 'ui-config', value: 6},
        {id: '', settingName: Settings.stBookingGridHeight, settingKey: 'stBookingGridHeight', value: null},
        {id: '', settingName: Settings.lookupNumberFormat, settingKey: 'ui-config', value: { prefix: '', suffix: '', typeFormat: LookupFormat.NoFormating }},
        {id: '', settingName: Settings.lookupSeparatorFormat, settingKey: 'ui-config', value: { value: ' ', typeFormat: LookupFormat.Space}},
        {id: '', settingName: Settings.lookupInfoFormat, settingKey: 'ui-config', value: { prefix: '', suffix: '', typeFormat: LookupFormat.NoFormating }},
        {id: '', settingName: Settings.fieldBorderHeight, settingKey: 'ui-config', value: 1},
        {id: '', settingName: Settings.fieldBorderHoverHeight, settingKey: 'ui-config', value: 1},
        {id: '', settingName: Settings.fieldBorderColor, settingKey: 'ui-config', value: 'none'},
        {id: '', settingName: Settings.formFieldType, settingKey: 'ui-config', value: 'none'},
        {id: '', settingName: Settings.ixCommonRuntime, settingKey: 'common-run-time', value: {
            userClass: '',userRight: '',objNr:  '',objNrFibu: '', yearDataIndex: '', year: '', group: '', grp_no: '', objRightsCheck: '', kreno: '',
            aktFlNr: '', aktAuftrag: '', aktSchaden: '', lper: '', lsac: '', prnName: '', verId: '', objNrId: '', // currentStatus: 'Rady',
            }
        },
        {id: '', settingName: Settings.gridHeaderColor, settingKey: 'ui-config', value: null},
        {id: '', settingName: Settings.gridHeaderTextColor, settingKey: 'ui-config', value: null},
        {id: '', settingName: Settings.gridRowColor, settingKey: 'ui-config', value: null},
        {id: '', settingName: Settings.gridRowTextColor, settingKey: 'ui-config', value: null},
        {id: '', settingName: Settings.gridBordersColor, settingKey: 'ui-config', value: null},
        {id: '', settingName: Settings.barsStyle, settingKey: 'ui-config', value: 'default-bars'},
        {id: '', settingName: Settings.compact, settingKey: 'ui-config', value: 'none'},
        {id: '', settingName: Settings.openLookupOnClick, settingKey: 'ui-config', value: false},
        {id: '', settingName: Settings.favoriteNavs, settingKey: 'menu-config', value: []},
    ],
    drawerState: window.innerWidth > 800 ? DrawerState.Open : DrawerState.Closed,
    actionPanelState: 'top' as PanelState,
    actions: [],
    tabsPanelState: 'top' as PanelState,
    tabs: [] as Tab[],
    activeTabIndex: 0,
    drawerTabIndex: 0,
    drawerWidth: 250,
    drawerFilter: null as any,
    tabsVisible: false,
    backgroundJobsData: [] as any,
    jobsInfo: {} as any,
    jobActionButtons: [],
    paddings: {
        OBJNR: 5,
        UNIT_NUMBER: 6,
        NR: 2,
        MWSTCODE: 3,
    } as {[k: string]: number},
})

const initialState = getInitialState()

const VALUEFORMAT = {
    [LookupFormat.NoFormating]: { prefix: '', suffix: '', typeFormat: LookupFormat.NoFormating },
    [LookupFormat.SquareBracket]: { prefix: '[', suffix: ']', typeFormat: LookupFormat.SquareBracket },
    [LookupFormat.CurlyBracket]: { prefix: '(', suffix: ')', typeFormat: LookupFormat.CurlyBracket },
    [LookupFormat.Space]: { value: ' ', typeFormat: LookupFormat.Space },
    [LookupFormat.Dash]: { value: ' - ', typeFormat: LookupFormat.Dash },
    [LookupFormat.Pipe]: { value: ' | ', typeFormat: LookupFormat.Pipe },
    [LookupFormat.DoublePipe]: { value: ' || ', typeFormat: LookupFormat.DoublePipe },
    [LookupFormat.Slash]: { value: ' / ', typeFormat: LookupFormat.Slash },
    [LookupFormat.DoubleSlash]: { value: ' // ', typeFormat: LookupFormat.DoubleSlash },
} as any

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setJobsInfo:(state, action) => {
            state.jobsInfo = {
                ...state.jobsInfo,
                ...action.payload
            }
        },
        toggleDrawer: (state, action) => {
            if (action.payload !== null) {
                state.drawerState = action.payload
            } else {
                if (Modernizr.hovermq) {
                    // if hover events are available show all modes
                    state.drawerState = state.drawerState !== DrawerState.Closed ? state.drawerState += 1 : DrawerState.Open
                } else {
                    // if hover is not available only enable Closed and Open states
                    state.drawerState = state.drawerState !== DrawerState.Open ? DrawerState.Open : DrawerState.Closed
                }
            }
        },
        setActionPanelState: (state, action) => {
            state.actionPanelState = action.payload
        },
        setBackgroundJobsData: (state, action) => {
            state.backgroundJobsData = action.payload
        },
        setTabsPanelState: (state, action) => {
            state.tabsPanelState = action.payload
        },
        setTabs: (state, action) => {
            state.tabs = action.payload
        },
        setTabsVisible: (state, action) => {
            state.tabsVisible = action.payload
        },
        setDrawerTabIndex: (state, action) => {
            state.drawerTabIndex = action.payload
        },
        setDrawerWidth: (state, action) => {
            state.drawerWidth = action.payload
        },
        setActiveTabIndex: (state, action) => {
            if (action.payload >= 0 && action.payload < state.tabs.length) {
            // if (action.payload >= 0) {

                state.activeTabIndex = action.payload
            } else {
                state.activeTabIndex = 0
            }
        },
        setDrawerFilter: (state, action) => {
            state.drawerFilter = action.payload
        },
        setJobActionButtons: (state, action) => {
            state.jobActionButtons = action.payload
        },
        setSettings: (state, action) => {
            const name = action.payload.settingName
            
            if (!state.settings.find((setting: any) => setting.settingName === name)) {
                state.settings = [...state.settings, {...action.payload}]
            }
            else {
                state.settings = state.settings.map((setting: any) => {
                    if (setting.settingName === name) {
                        if (name === 'lookupNumberFormat' || name === 'lookupSeparatorFormat' || name === 'lookupInfoFormat') {
                            return {...setting, ...action.payload, value: VALUEFORMAT[action.payload.value]}
                        }
                        else { return {...setting, ...action.payload} }
                    }
                    else { return setting}
                })

                if (name === 'objNumber' || name === 'unitNumber') {
                    const paddings: any = {objNumber: 'OBJNR', unitNumber: 'UNIT_NUMBER'}
                    state.paddings = {...state.paddings, [paddings[name]]: action.payload.value}
                }

            }
        },
        resetSettings: (state) => {
            const initialState = getInitialState()

            state.tabsPanelState = initialState.tabsPanelState
            state.tabs = initialState.tabs
            state.activeTabIndex = initialState.activeTabIndex
            state.drawerTabIndex = initialState.drawerTabIndex
            state.drawerWidth = initialState.drawerWidth
            state.drawerFilter = initialState.drawerFilter
            state.tabsVisible = initialState.tabsVisible
            state.backgroundJobsData = initialState.backgroundJobsData
            state.jobsInfo = initialState.jobsInfo
            state.jobActionButtons = initialState.jobActionButtons
            state.settings = initialState.settings
        }
    }
})

export const {
    toggleDrawer, setTabsPanelState, setTabs, setActiveTabIndex, setActionPanelState,
    setJobActionButtons, setTabsVisible, setDrawerTabIndex, setDrawerWidth,
    setBackgroundJobsData, setJobsInfo, setDrawerFilter, resetSettings, setSettings
} = uiSlice.actions

export default uiSlice.reducer

